import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { fetchMe } from '../lib/me.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { StatusPill } from '../components/StatusPill.js'
import { Money } from '../components/Money.js'
import { Icon, type IconName } from '../components/Icon.js'
import { Empty } from '../components/Empty.js'

export const Route = createFileRoute('/_app/dashboard')({
  beforeLoad: async () => {
    // Redirect parents straight to their children list — that's their home.
    // Super admin home is handled by sidebar nav (no '/' route for them).
    const me = await fetchMe()
    if (!me) return
    // Brand-new self-signup (no academy, no children) -> academy onboarding.
    if (me.needsOnboarding) {
      throw redirect({ to: '/onboarding/register' })
    }
    if (me.role === 'parent') {
      throw redirect({ to: '/children' })
    }
    if (me.role === 'teacher') {
      throw redirect({ to: '/my-classes' })
    }
    if (me.role === 'super_admin') {
      throw redirect({ to: '/super/academies' })
    }
  },
  component: Dashboard,
})

interface MeResponse {
  id: string
  email: string
  role: string
  academyId?: string
}

interface AcademyResponse {
  id: string
  name: string
  slug: string
  status: string
  features: Record<string, boolean>
}

interface StudentRow {
  id: string
  isActive: boolean
}
interface ClassRow {
  id: string
  isActive: boolean
}
interface TeacherRow {
  userId: string
}
interface InvoiceRow {
  id: string
  studentName: string
  finalAmountInr: number
  dueDate: string
  status: 'pending' | 'paid' | 'overdue' | 'waived'
}

function Dashboard() {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const result = await api<MeResponse>('/api/v1/me')
      if ('error' in result) throw new Error(result.error.message)
      return result.data
    },
  })

  const academy = useQuery({
    queryKey: ['academy'],
    enabled: Boolean(me.data?.academyId),
    queryFn: async () => {
      const result = await api<AcademyResponse>('/api/v1/academy')
      if ('error' in result) throw new Error(result.error.message)
      return result.data
    },
  })

  const students = useQuery({
    queryKey: ['students'],
    enabled: Boolean(me.data?.academyId),
    queryFn: async () => {
      const res = await api<StudentRow[]>('/api/v1/students')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const classes = useQuery({
    queryKey: ['classes'],
    enabled: Boolean(me.data?.academyId),
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const teachers = useQuery({
    queryKey: ['teachers'],
    enabled: Boolean(me.data?.academyId),
    queryFn: async () => {
      const res = await api<TeacherRow[]>('/api/v1/teachers')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const invoices = useQuery({
    queryKey: ['fee-invoices'],
    enabled: Boolean(me.data?.academyId),
    queryFn: async () => {
      const res = await api<InvoiceRow[]>('/api/v1/fee-invoices')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const activeStudents =
    students.data?.filter((s) => s.isActive).length ?? null
  const activeClasses = classes.data?.filter((c) => c.isActive).length ?? null
  const teacherCount = teachers.data?.length ?? null

  const outstanding =
    invoices.data?.filter((i) => i.status === 'pending' || i.status === 'overdue') ?? []
  const outstandingTotal = outstanding.reduce((sum, i) => sum + i.finalAmountInr, 0)
  const recentOutstanding = [...outstanding]
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .slice(0, 5)

  return (
    <div className="space-y-6">
      <PageHeader
        title={academy.data ? academy.data.name : 'Overview'}
        description={
          academy.data ? (
            <>
              <span className="font-mono text-xs">
                {academy.data.slug}.brightstars.in
              </span>{' '}
              · <StatusPill tone={academy.data.status}>{academy.data.status}</StatusPill>
            </>
          ) : (
            'A summary of your academy.'
          )
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          icon="users"
          label="Active students"
          value={activeStudents}
          to="/students"
        />
        <KpiTile
          icon="graduationCap"
          label="Classes"
          value={activeClasses}
          to="/classes"
        />
        <KpiTile
          icon="users"
          label="Teachers"
          value={teacherCount}
          to="/teachers"
        />
        <KpiTile
          icon="wallet"
          label="Outstanding"
          value={outstanding.length}
          subValue={
            invoices.data ? (
              <Money paise={outstandingTotal} />
            ) : null
          }
          tone={outstanding.length > 0 ? 'warning' : 'success'}
          to="/fees"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Invoices to chase"
            description="Earliest due first"
            actions={
              <Link
                to="/fees"
                className="text-sm text-[var(--color-primary)] hover:underline"
              >
                View all
              </Link>
            }
          />
          {recentOutstanding.length === 0 ? (
            <Empty
              icon="check"
              title={
                invoices.isLoading ? 'Loading…' : 'No outstanding invoices'
              }
              description={
                invoices.isLoading
                  ? undefined
                  : 'Every parent is paid up. Nice work.'
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {recentOutstanding.map((inv) => (
                <li
                  key={inv.id}
                  className="px-5 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg truncate">
                      {inv.studentName}
                    </p>
                    <p className="text-xs text-fg-subtle">
                      Due{' '}
                      {new Date(inv.dueDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Money paise={inv.finalAmountInr} variant="data" />
                    <StatusPill tone={inv.status}>{inv.status}</StatusPill>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Quick actions" />
          <CardBody className="space-y-2">
            <QuickAction
              to="/students/new"
              icon="plus"
              label="Add a student"
              description="Onboard a new parent"
            />
            <QuickAction
              to="/fees/new"
              icon="wallet"
              label="Raise an invoice"
              description="Bill a fee category"
            />
            <QuickAction
              to="/tests"
              icon="clipboardCheck"
              label="Create a test"
              description="Score students later"
            />
            <QuickAction
              to="/settings/payments"
              icon="settings"
              label="Payment setup"
              description="Razorpay keys + brand"
            />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

function KpiTile({
  icon,
  label,
  value,
  subValue,
  to,
  tone,
}: {
  icon: IconName
  label: string
  value: number | null
  subValue?: React.ReactNode
  to?: string
  tone?: 'warning' | 'success'
}) {
  const ring =
    tone === 'warning'
      ? 'bg-warning-tint text-warning'
      : tone === 'success'
        ? 'bg-success-tint text-success'
        : 'bg-surface-subtle text-fg-muted'
  const inner = (
    <Card className="h-full transition-colors hover:border-border-strong">
      <CardBody>
        <div className="flex items-start justify-between">
          <span
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${ring}`}
          >
            <Icon name={icon} size={16} />
          </span>
          {to ? (
            <Icon name="chevronRight" size={14} className="text-fg-faint" />
          ) : null}
        </div>
        <p className="mt-3 text-xs text-fg-subtle">{label}</p>
        <p className="mt-1 text-2xl font-semibold text-fg tabular-nums">
          {value === null ? '—' : value}
        </p>
        {subValue ? (
          <p className="mt-0.5 text-xs text-fg-subtle">{subValue}</p>
        ) : null}
      </CardBody>
    </Card>
  )
  return to ? (
    <Link to={to} className="block">
      {inner}
    </Link>
  ) : (
    inner
  )
}

function QuickAction({
  to,
  icon,
  label,
  description,
}: {
  to: string
  icon: IconName
  label: string
  description: string
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-surface-subtle transition-colors"
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface-subtle text-fg-muted group-hover:bg-[var(--color-primary-tint)] group-hover:text-[var(--color-primary)] transition-colors">
        <Icon name={icon} size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{label}</p>
        <p className="text-xs text-fg-subtle truncate">{description}</p>
      </div>
      <Icon name="chevronRight" size={14} className="text-fg-faint" />
    </Link>
  )
}
